import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';

import type { ContactShowSchema } from '@liscio/api';
import type { AccountGroupAccount } from '@liscio/common';
import { Icon } from '@liscio/ui';

import { useFirmsAccountsList } from '../../fetch-utils/contacts/contacts-v5-hooks';
import {
  useAssignAccountToContact,
  useUpdateFirmContact,
} from '../../fetch-utils/contacts/contacts-v5-mutations';
import {
  CreateContactFormFields,
  useCreateContactForm,
  useCreateContactSubmit,
} from '../CreateContactForm';
import type { ContactSuggestion } from '../CreateContactForm';
import {
  getContactDisplayName,
  getContactInitials,
} from '../CreateContactForm/groupContactSuggestions';

export interface ExistingContactSelection {
  contact_id: number;
  contact_uuid: string;
  cpa_user_id?: number | null;
  first_name?: string | null;
  last_name?: string | null;
  email: string;
  account_id: number;
  account_name: string;
}

type PanelState = 'new-contact' | 'preview' | 'edit';

interface InlineCreateContactPanelProps {
  account: AccountGroupAccount;
  fixedAccountId?: number;
  onBack: () => void;
  onCancel: () => void;
  onSuccess: (contact: ContactShowSchema) => void;
  onSelectExistingContact: (selection: ExistingContactSelection) => void;
  isExistingContactSelected?: (contactId: number, accountId: number) => boolean;
}

function getApiErrorMessage(error: unknown, fallback: string): string {
  const apiError = error as {
    response?: { data?: { error?: string; details?: string[] } };
    message?: string;
  };
  return (
    apiError.response?.data?.error ??
    apiError.response?.data?.details?.join(', ') ??
    apiError.message ??
    fallback
  );
}

function HeaderAccountChipDropdown({
  accountId,
  accountName,
  accounts,
  disabled,
  onChange,
}: {
  accountId: number;
  accountName: string;
  accounts: Array<{ id: number; name: string }>;
  disabled?: boolean;
  onChange: (id: number) => void;
}) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  return (
    <>
      <Button
        size="small"
        disabled={disabled}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        endIcon={<Icon icon="chevronDown" fontSize="small" />}
        sx={{
          textTransform: 'none',
          borderRadius: 999,
          border: 1,
          borderColor: 'divider',
          px: 1.5,
          py: 0.5,
          fontSize: '0.8125rem',
          color: 'text.primary',
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}
      >
        Under{' '}
        <Box component="span" sx={{ fontWeight: 700, ml: 0.5 }}>
          {accountName}
        </Box>
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        PaperProps={{ sx: { maxHeight: 280, minWidth: 220 } }}
      >
        {accounts.map((option) => (
          <MenuItem
            key={option.id}
            selected={option.id === accountId}
            onClick={() => {
              onChange(option.id);
              setAnchorEl(null);
            }}
          >
            {option.name}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

export function InlineCreateContactPanel({
  account,
  fixedAccountId,
  onBack,
  onCancel,
  onSuccess,
  onSelectExistingContact,
  isExistingContactSelected,
}: InlineCreateContactPanelProps) {
  const formMethods = useCreateContactForm({ emailRequired: false });
  const [panelState, setPanelState] = useState<PanelState>('new-contact');
  const [headerAccountId, setHeaderAccountId] = useState<number>(account.id);
  const [previewContact, setPreviewContact] =
    useState<ContactSuggestion | null>(null);
  const [editAccountId, setEditAccountId] = useState<number>(account.id);

  const { data: accountOptions = [], isLoading: isLoadingAccounts } =
    useFirmsAccountsList();
  const { mutateAsync: assignAccountToContact, isLoading: isAssigningAccount } =
    useAssignAccountToContact();
  const { mutateAsync: updateFirmContact, isLoading: isUpdatingContact } =
    useUpdateFirmContact();

  const editForm = useForm({
    defaultValues: { first_name: '', last_name: '' },
  });

  useEffect(() => {
    setHeaderAccountId(account.id);
    setEditAccountId(account.id);
  }, [account.id]);

  const accounts = useMemo(() => {
    if (fixedAccountId != null) {
      return [
        {
          id: account.id,
          name: account.name,
          external_account_id: null,
          contacts_count: 0,
        },
      ];
    }

    const hasClickedAccount = accountOptions.some(
      (option) => option.id === account.id
    );
    if (hasClickedAccount) return accountOptions;

    return [
      {
        id: account.id,
        name: account.name,
        external_account_id: null,
        contacts_count: 0,
      },
      ...accountOptions,
    ];
  }, [account.id, account.name, accountOptions, fixedAccountId]);

  const headerAccount = useMemo(
    () =>
      accounts.find((option) => option.id === headerAccountId) ?? {
        id: account.id,
        name: account.name,
      },
    [account.id, account.name, accounts, headerAccountId]
  );

  const editAccount = useMemo(
    () =>
      accounts.find((option) => option.id === editAccountId) ?? headerAccount,
    [accounts, editAccountId, headerAccount]
  );

  const { submitCreateContact, isSubmitting } = useCreateContactSubmit(
    formMethods,
    {
      source: 'account_contact_selector',
      accountId: String(headerAccount.id),
      onSuccess: (contact) => {
        onSuccess(contact);
      },
    }
  );

  const isAccountLinked = useCallback(
    (contact: ContactSuggestion, accountId: number) =>
      contact.account_ids?.includes(accountId) ?? false,
    []
  );

  const confirmExistingContact = useCallback(
    async ({
      contact,
      accountId,
      accountName,
      firstName,
      lastName,
      skipUpdate = true,
      updatePayload,
    }: {
      contact: ContactSuggestion;
      accountId: number;
      accountName: string;
      firstName?: string | null;
      lastName?: string | null;
      skipUpdate?: boolean;
      updatePayload?: { first_name: string; last_name: string };
    }) => {
      if (!contact.contact_id || !contact.contact_uuid) {
        toast.error('Could not select this contact.');
        return;
      }

      if (isExistingContactSelected?.(contact.contact_id, accountId)) {
        onSelectExistingContact({
          contact_id: contact.contact_id,
          contact_uuid: contact.contact_uuid,
          cpa_user_id: contact.cpa_user_id,
          first_name: firstName ?? contact.first_name,
          last_name: lastName ?? contact.last_name,
          email: contact.primary_email,
          account_id: accountId,
          account_name: accountName,
        });
        return;
      }

      if (updatePayload && !skipUpdate) {
        try {
          await updateFirmContact({
            // The update endpoint resolves :id by UUID (assign_account uses the
            // numeric id), so send contact_uuid here, not contact_id.
            contactId: contact.contact_uuid,
            data: {
              first_name: updatePayload.first_name,
              last_name: updatePayload.last_name,
            },
          });
        } catch (error) {
          toast.error(
            getApiErrorMessage(error, 'Could not update this contact.')
          );
          return;
        }
      }

      if (!isAccountLinked(contact, accountId)) {
        try {
          await assignAccountToContact({
            contactId: String(contact.contact_id),
            accountId,
          });
        } catch (error) {
          toast.error(
            getApiErrorMessage(
              error,
              'Could not associate this contact with the selected account.'
            )
          );
          return;
        }
      }

      onSelectExistingContact({
        contact_id: contact.contact_id,
        contact_uuid: contact.contact_uuid,
        cpa_user_id: contact.cpa_user_id,
        first_name: firstName ?? contact.first_name,
        last_name: lastName ?? contact.last_name,
        email: contact.primary_email,
        account_id: accountId,
        account_name: accountName,
      });
    },
    [
      assignAccountToContact,
      isAccountLinked,
      isExistingContactSelected,
      onSelectExistingContact,
      updateFirmContact,
    ]
  );

  const handlePreviewExistingContact = useCallback(
    (suggestion: ContactSuggestion) => {
      setPreviewContact(suggestion);
      setPanelState('preview');
    },
    []
  );

  const handleOpenEdit = useCallback(() => {
    if (!previewContact) return;
    setEditAccountId(headerAccountId);
    editForm.reset({
      first_name: previewContact.first_name ?? '',
      last_name: previewContact.last_name ?? '',
    });
    setPanelState('edit');
  }, [editForm, headerAccountId, previewContact]);

  const handleSelectContact = useCallback(async () => {
    if (!previewContact) return;
    await confirmExistingContact({
      contact: previewContact,
      accountId: headerAccount.id,
      accountName: headerAccount.name,
    });
  }, [confirmExistingContact, headerAccount, previewContact]);

  const handleSaveAndSelect = useCallback(async () => {
    if (!previewContact) return;

    const values = editForm.getValues();
    if (!values.first_name.trim() || !values.last_name.trim()) {
      toast.error('First and last name are required.');
      return;
    }

    await confirmExistingContact({
      contact: previewContact,
      accountId: editAccount.id,
      accountName: editAccount.name,
      firstName: values.first_name.trim(),
      lastName: values.last_name.trim(),
      skipUpdate: false,
      updatePayload: {
        first_name: values.first_name.trim(),
        last_name: values.last_name.trim(),
      },
    });
  }, [confirmExistingContact, editAccount, editForm, previewContact]);

  const isBusy = isSubmitting || isAssigningAccount || isUpdatingContact;

  const headerTitle =
    panelState === 'preview'
      ? 'Add existing contact'
      : panelState === 'edit'
      ? 'Edit contact'
      : 'New contact';

  const handleHeaderBack = () => {
    if (panelState === 'edit') {
      setPanelState('preview');
      return;
    }
    if (panelState === 'preview') {
      setPreviewContact(null);
      setPanelState('new-contact');
      return;
    }
    onBack();
  };

  const showAssociationNote =
    panelState === 'preview' &&
    previewContact &&
    !isAccountLinked(previewContact, headerAccount.id);

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <IconButton
          size="small"
          onClick={handleHeaderBack}
          aria-label="Back"
          sx={{ flexShrink: 0 }}
        >
          <Icon icon="arrowLeft" fontSize="small" />
        </IconButton>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 700, flex: 1, textAlign: 'center' }}
        >
          {headerTitle}
        </Typography>
        {panelState === 'new-contact' ? (
          <HeaderAccountChipDropdown
            accountId={headerAccount.id}
            accountName={headerAccount.name}
            accounts={accounts}
            disabled={isLoadingAccounts || isBusy}
            onChange={setHeaderAccountId}
          />
        ) : (
          <Box sx={{ width: 40, flexShrink: 0 }} />
        )}
      </Box>

      <Box sx={{ px: 2, py: 2, maxHeight: 360, overflow: 'auto' }}>
        {panelState === 'new-contact' && (
          <FormProvider {...formMethods}>
            <CreateContactFormFields
              layout="responsive"
              emailRequired={false}
              enableSuggestions
              hideDetailsUntilRevealed
              primaryEmailLabel="Primary Email — used to send the portal invite"
              onSelectExistingContact={handlePreviewExistingContact}
              showInviteContactCheckbox
            />
          </FormProvider>
        )}

        {panelState === 'preview' && previewContact && (
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: 'block',
                mb: 0.75,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}
            >
              Will be added to account
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 1.25,
                mb: 2,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                bgcolor: 'grey.50',
              }}
            >
              <Icon icon="accounts" fontSize="small" />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {headerAccount.name}
              </Typography>
            </Box>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: 'block',
                mb: 0.75,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}
            >
              Contact
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                p: 1.5,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  fontSize: '0.875rem',
                  fontWeight: 700,
                  bgcolor: 'grey.800',
                  flexShrink: 0,
                }}
              >
                {getContactInitials(previewContact)}
              </Avatar>
              <Box flex={1} minWidth={0}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flexWrap: 'wrap',
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {getContactDisplayName(previewContact)}
                  </Typography>
                  <Chip
                    label="Already in Liscio"
                    size="small"
                    variant="outlined"
                    color="primary"
                    sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600 }}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {previewContact.primary_email}
                </Typography>
              </Box>
              <Button
                size="small"
                variant="outlined"
                onClick={handleOpenEdit}
                sx={{ textTransform: 'none', flexShrink: 0 }}
              >
                Edit
              </Button>
            </Box>

            {showAssociationNote && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 1.5 }}
              >
                Selecting this contact will also associate them with{' '}
                {headerAccount.name}.
              </Typography>
            )}
          </Box>
        )}

        {panelState === 'edit' && previewContact && (
          <Box component="form" noValidate>
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel id="edit-existing-contact-account-label">
                Account
              </InputLabel>
              <Select
                labelId="edit-existing-contact-account-label"
                label="Account"
                value={String(editAccount.id)}
                disabled={isLoadingAccounts || isBusy}
                onChange={(event: SelectChangeEvent) => {
                  setEditAccountId(Number(event.target.value));
                }}
              >
                {accounts.map((option) => (
                  <MenuItem key={option.id} value={String(option.id)}>
                    {option.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                label="First Name"
                required
                fullWidth
                size="small"
                {...editForm.register('first_name', { required: true })}
              />
              <TextField
                label="Last Name"
                required
                fullWidth
                size="small"
                {...editForm.register('last_name', { required: true })}
              />
            </Box>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: 'block',
                mb: 0.75,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}
            >
              Primary Email
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={previewContact.primary_email}
              disabled
              sx={{ mb: 1 }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Icon
                icon="emails"
                sx={{ fontSize: 16, color: 'text.secondary' }}
              />
              <Typography variant="caption" color="text.secondary">
                Email can&apos;t be changed on an existing contact.
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          borderTop: 1,
          borderColor: 'divider',
          px: 2,
          py: 1.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {panelState === 'edit' ? (
          <Button
            onClick={() => setPanelState('preview')}
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            Back
          </Button>
        ) : (
          <Button
            onClick={
              panelState === 'preview'
                ? () => {
                    setPreviewContact(null);
                    setPanelState('new-contact');
                  }
                : onCancel
            }
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            Cancel
          </Button>
        )}

        {panelState === 'new-contact' && (
          <Button
            variant="contained"
            onClick={submitCreateContact}
            disabled={isBusy}
            startIcon={
              isSubmitting ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
            sx={{ textTransform: 'none' }}
          >
            Create & select
          </Button>
        )}

        {panelState === 'preview' && (
          <Button
            variant="contained"
            onClick={handleSelectContact}
            disabled={isBusy}
            startIcon={
              isAssigningAccount ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
            sx={{ textTransform: 'none' }}
          >
            Select contact
          </Button>
        )}

        {panelState === 'edit' && (
          <Button
            variant="contained"
            onClick={handleSaveAndSelect}
            disabled={isBusy}
            startIcon={
              isBusy ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
            sx={{ textTransform: 'none' }}
          >
            Save & select
          </Button>
        )}
      </Box>
    </Box>
  );
}
